// /apps/patient/src/pages/billing/PaymentPage.tsx

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { patientApiService } from '../../services/api';
import { PageHeader } from '../../components/layout/PageHeader';
import { Invoice, PaymentMethod } from '@prism-ehr/shared';
import { formatDate } from '@prism-ehr/shared';
import {
  CreditCard, Loader2, CheckCircle, AlertCircle,
  Building, ArrowRight, Package, Calendar
} from 'lucide-react';

// Types for different payment contexts
interface PaymentPageState {
  type: 'invoice' | 'package' | 'subscription' | 'class_enrollment';
  packageId?: string;
  planId?: string;
  amount?: number;
  description?: string;
  packageData?: any;
  planData?: any;
  class_session_id?: string;
  class_name?: string;
}

interface PaymentFormProps {
  paymentDetails: {
    amount: number;
    description: string;
    type: 'invoice' | 'package' | 'subscription' | 'class_enrollment';
    invoice?: Invoice;
    packageData?: any;
    planData?: any;
  };
  onSuccess: () => void;
  savedPaymentMethods: PaymentMethod[];
  stripeConfig: any;
  stripe: any;
}

// Inner form component for new card payments
function NewCardPaymentForm({ paymentDetails, onSuccess, stripeConfig, savePaymentMethod, onSavePaymentMethodChange }: any) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
  
    if (!stripe || !elements) return;
  
    setIsProcessing(true);
    setErrorMessage('');
  
    try {
      // Submit elements first (new Stripe requirement)
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || 'Failed to submit payment details');
        setIsProcessing(false);
        return;
      }
  
      let clientSecret;
      
      // Create payment intent based on type
      if (paymentDetails.type === 'invoice' && paymentDetails.invoice) {
        const paymentIntentResponse = await patientApiService.createPaymentIntent({
          invoice_id: paymentDetails.invoice.id,
          save_payment_method: savePaymentMethod
        });
        clientSecret = paymentIntentResponse.client_secret;
      } else if (paymentDetails.type === 'package' && paymentDetails.packageData) {
        const response = await patientApiService.purchasePackage({
          package_id: paymentDetails.packageData.id,
          save_payment_method: savePaymentMethod
        });
        clientSecret = response.client_secret;
      } else if (paymentDetails.type === 'subscription' && paymentDetails.planData) {
        const response = await patientApiService.subscribeToPlan({
          plan_id: paymentDetails.planData.id,
          save_payment_method: savePaymentMethod
        });
        clientSecret = response.client_secret;

        if (!clientSecret && (response.status === 'trialing' || response.status === 'active')) {
          onSuccess();
          return;
        }
      }

      if (!clientSecret) {
        throw new Error('Failed to create payment intent');
      }

      // Confirm payment
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/billing/payment-success/${paymentDetails.invoice?.id || 'complete'}?type=${paymentDetails.type}`,
        },
        redirect: 'if_required'
      });

      if (error) {
        setErrorMessage(error.message || 'Payment failed');
        return;
      }

      // Payment succeeded
      onSuccess();
    } catch (error) {
      console.error('Payment error:', error);
      setErrorMessage('An error occurred processing your payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement
        options={{
          layout: 'tabs',
          paymentMethodOrder: ['card'],
          fields: {
            billingDetails: {
              email: 'auto'
            }
          }
        }}
      />

      {/* Save payment method checkbox */}
      <label className="flex items-center">
        <input
          type="checkbox"
          checked={savePaymentMethod}
          onChange={(e) => onSavePaymentMethodChange(e.target.checked)}
          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
        />
        <span className="ml-2 text-sm text-gray-600">
          Save this payment method for future use
        </span>
      </label>

      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{errorMessage}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="mr-2 h-5 w-5" />
            Pay {formatCurrency(paymentDetails.amount)}
          </>
        )}
      </button>
    </form>
  );
}

function PaymentForm({ paymentDetails, onSuccess, savedPaymentMethods, stripeConfig, stripe }: PaymentFormProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [savePaymentMethod, setSavePaymentMethod] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleSavedCardPayment = async () => {
    if (!selectedPaymentMethodId || !stripe) return;

    setIsProcessing(true);
    setErrorMessage('');

    try {
      let clientSecret;
      
      if (paymentDetails.type === 'invoice' && paymentDetails.invoice) {
        const paymentIntentResponse = await patientApiService.createPaymentIntent({
          invoice_id: paymentDetails.invoice.id,
          payment_method_id: selectedPaymentMethodId,
          save_payment_method: false
        });
        clientSecret = paymentIntentResponse.client_secret;
      } else if (paymentDetails.type === 'package' && paymentDetails.packageData) {
        const response = await patientApiService.purchasePackage({
          package_id: paymentDetails.packageData.id,
          payment_method_id: selectedPaymentMethodId
        });
        clientSecret = response.client_secret;
      } else if (paymentDetails.type === 'subscription' && paymentDetails.planData) {
        const response = await patientApiService.subscribeToPlan({
          plan_id: paymentDetails.planData.id,
          payment_method_id: selectedPaymentMethodId
        });
        clientSecret = response.client_secret;

        if (!clientSecret && (response.status === 'trialing' || response.status === 'active')) {
          onSuccess();
          return;
        }
      }

      if (!clientSecret) {
        throw new Error('Failed to create payment intent');
      }

      const { error } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: selectedPaymentMethodId
        }
      );

      if (error) {
        setErrorMessage(error.message || 'Payment failed');
        return;
      }

      // Payment succeeded
      onSuccess();
    } catch (error) {
      console.error('Payment error:', error);
      setErrorMessage('An error occurred processing your payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // If showing new payment form
  if (showPaymentForm && !selectedPaymentMethodId) {
    return (
      <div className="space-y-6">
        {/* Payment Summary */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="font-medium text-gray-900 mb-4">Payment Summary</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center">
                {paymentDetails.type === 'package' && <Package className="h-4 w-4 mr-2 text-gray-400" />}
                {paymentDetails.type === 'subscription' && <Calendar className="h-4 w-4 mr-2 text-gray-400" />}
                <span className="text-gray-600">
                  {paymentDetails.type.charAt(0).toUpperCase() + paymentDetails.type.slice(1)}
                </span>
              </div>
              <span className="font-medium">{paymentDetails.description}</span>
            </div>
            
            {paymentDetails.type === 'invoice' && paymentDetails.invoice && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Invoice Number</span>
                  <span className="font-medium">#{paymentDetails.invoice.invoice_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Service Date</span>
                  <span className="font-medium">
                    {formatDate(paymentDetails.invoice.service_date || paymentDetails.invoice.created_at, 'short')}
                  </span>
                </div>
              </>
            )}
            
            {paymentDetails.type === 'package' && paymentDetails.packageData && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Credits</span>
                <span className="font-medium">{paymentDetails.packageData.total_credits} visits</span>
              </div>
            )}
            
            {paymentDetails.type === 'subscription' && paymentDetails.planData && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Billing Period</span>
                  <span className="font-medium capitalize">{paymentDetails.planData.billing_period}</span>
                </div>
                {paymentDetails.planData.trial_days > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Free Trial</span>
                    <span className="font-medium">{paymentDetails.planData.trial_days} days</span>
                  </div>
                )}
              </>
            )}
            
            <div className="border-t pt-3">
              <div className="flex justify-between text-base font-medium">
                <span>Amount Due</span>
                <span className="text-xl text-primary">{formatCurrency(paymentDetails.amount)}</span>
              </div>
            </div>
          </div>
        </div>

        <Elements
          stripe={stripe}
          options={{
            mode: 'payment',
            amount: Math.round(paymentDetails.amount * 100),
            currency: 'usd',
            // Must match the PaymentIntent: the intent only carries off_session when the
            // patient opts to save the card, except subscriptions which always save it.
            setupFutureUsage:
              paymentDetails.type === 'subscription' || savePaymentMethod ? 'off_session' : null,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#0fadc7'
              }
            }
          }}
        >
          <NewCardPaymentForm
            paymentDetails={paymentDetails}
            onSuccess={onSuccess}
            stripeConfig={stripeConfig}
            savePaymentMethod={savePaymentMethod}
            onSavePaymentMethodChange={setSavePaymentMethod}
          />
        </Elements>
      </div>
    );
  }

  // Initial form for selecting payment method
  return (
    <div className="space-y-6">
      {/* Payment Summary */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="font-medium text-gray-900 mb-4">Payment Summary</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center">
              {paymentDetails.type === 'package' && <Package className="h-4 w-4 mr-2 text-gray-400" />}
              {paymentDetails.type === 'subscription' && <Calendar className="h-4 w-4 mr-2 text-gray-400" />}
              <span className="text-gray-600">
                {paymentDetails.type.charAt(0).toUpperCase() + paymentDetails.type.slice(1)}
              </span>
            </div>
            <span className="font-medium">{paymentDetails.description}</span>
          </div>
          
          {paymentDetails.type === 'invoice' && paymentDetails.invoice && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Invoice Number</span>
                <span className="font-medium">#{paymentDetails.invoice.invoice_number}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Service Date</span>
                <span className="font-medium">
                  {formatDate(paymentDetails.invoice.service_date || paymentDetails.invoice.created_at, 'short')}
                </span>
              </div>
            </>
          )}
          
          {paymentDetails.type === 'package' && paymentDetails.packageData && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Credits</span>
              <span className="font-medium">{paymentDetails.packageData.total_credits} visits</span>
            </div>
          )}
          
          {paymentDetails.type === 'subscription' && paymentDetails.planData && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Billing Period</span>
                <span className="font-medium capitalize">{paymentDetails.planData.billing_period}</span>
              </div>
              {paymentDetails.planData.trial_days > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Free Trial</span>
                  <span className="font-medium">{paymentDetails.planData.trial_days} days</span>
                </div>
              )}
            </>
          )}
          
          <div className="border-t pt-3">
            <div className="flex justify-between text-base font-medium">
              <span>Amount Due</span>
              <span className="text-xl text-primary">{formatCurrency(paymentDetails.amount)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Saved Payment Methods */}
      {savedPaymentMethods.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-900">Saved Payment Methods</h4>
          {savedPaymentMethods.map((method) => (
            <label
              key={method.id}
              className={`relative flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                selectedPaymentMethodId === method.stripe_payment_method_id
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="payment_method"
                value={method.stripe_payment_method_id}
                checked={selectedPaymentMethodId === method.stripe_payment_method_id}
                onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                className="sr-only"
              />
              <div className="flex items-center flex-1">
                <CreditCard className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {method.card_brand} •••• {method.card_last_four}
                  </p>
                  <p className="text-xs text-gray-500">
                    Expires {method.card_exp_month}/{method.card_exp_year}
                  </p>
                </div>
                {method.is_default && (
                  <span className="ml-auto text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                    Default
                  </span>
                )}
              </div>
            </label>
          ))}

          <button
            type="button"
            onClick={() => setSelectedPaymentMethodId(null)}
            className="text-sm text-primary hover:text-primary-dark"
          >
            Use a new payment method
          </button>
        </div>
      )}

      {/* Placeholder for new payment method */}
      {!selectedPaymentMethodId && savedPaymentMethods.length === 0 && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">
            Click continue to enter payment details
          </p>
        </div>
      )}

      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{errorMessage}</p>
        </div>
      )}

      {/* Action Button */}
      <button
        onClick={() => {
          if (selectedPaymentMethodId) {
            handleSavedCardPayment();
          } else {
            setShowPaymentForm(true);
          }
        }}
        disabled={isProcessing}
        className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Processing...
          </>
        ) : selectedPaymentMethodId ? (
          <>
            <CreditCard className="mr-2 h-5 w-5" />
            Pay {formatCurrency(paymentDetails.amount)}
          </>
        ) : (
          <>
            <ArrowRight className="mr-2 h-5 w-5" />
            Continue to Payment
          </>
        )}
      </button>

      {/* Security Notice */}
      <div className="text-center text-xs text-gray-500">
        <p className="flex items-center justify-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Your payment information is secure and encrypted
        </p>
      </div>
    </div>
  );
}

export function PaymentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [stripeConfig, setStripeConfig] = useState<any>(null);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);

  const locationState = location.state as PaymentPageState;

  useEffect(() => {
    loadData();
  }, [id, locationState]);

  const loadData = async () => {
    try {
      const [paymentMethodsData, configData] = await Promise.all([
        patientApiService.getMyPaymentMethods().catch(() => ({ payment_methods: [] })),
        patientApiService.getStripeConfig()
      ]);

      setSavedPaymentMethods(paymentMethodsData.payment_methods || []);
      setStripeConfig(configData);

      // Initialize Stripe
      const stripe = await loadStripe(configData.stripe_publishable_key, {
        stripeAccount: configData.stripe_account_id
      });
      setStripePromise(stripe);

      // Load payment details based on context
      if (id && !locationState) {
        // Invoice payment
        const invoiceData = await patientApiService.getInvoiceDetail(id);
        
        if (invoiceData.status === 'paid') {
          alert('This invoice has already been paid');
          navigate(`/billing/invoices/${id}`);
          return;
        }

        setInvoice(invoiceData);
        setPaymentDetails({
          amount: invoiceData.balance_due,
          description: `Invoice #${invoiceData.invoice_number}`,
          type: 'invoice',
          invoice: invoiceData
        });
      } else if (locationState?.type === 'package') {
        // Package payment
        const packageData = locationState.packageData || 
          await patientApiService.getAvailablePackages()
            .then(packages => packages.find(p => p.id === locationState.packageId));
            
        setPaymentDetails({
          amount: packageData.price,
          description: packageData.name,
          type: 'package',
          packageData
        });
      } else if (locationState?.type === 'subscription') {
        // Subscription payment
        const planData = locationState.planData || 
          await patientApiService.getAvailableSubscriptionPlans()
            .then(plans => plans.find(p => p.id === locationState.planId));
            
        setPaymentDetails({
          amount: planData.price,
          description: `${planData.name} (${planData.billing_period})`,
          type: 'subscription',
          planData
        });
      }
    } catch (error) {
      console.error('Error loading payment page:', error);
      alert('Failed to load payment page');
      navigate('/billing');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    if (paymentDetails.type === 'invoice') {
      navigate(`/billing/payment-success/${id}`, { replace: true });
    } else {
      navigate('/billing/payment-success/complete', { 
        state: { 
          type: paymentDetails.type,
          amount: paymentDetails.amount,
          description: paymentDetails.description
        },
        replace: true 
      });
    }
  };

  if (loading || !paymentDetails || !stripePromise) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-gray-500 mt-2">Loading payment system...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader 
          title={
            paymentDetails.type === 'invoice' 
              ? 'Pay Invoice' 
              : paymentDetails.type === 'package'
              ? 'Purchase Package'
              : 'Subscribe to Plan'
          } 
          showBack={true} 
        />

        <div className="mt-6 bg-white rounded-lg shadow p-6 sm:p-8">
          {/* Organization Info */}
          {invoice && (
            <div className="mb-6 pb-6 border-b">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Building className="h-4 w-4" />
                <span>Paying to: <span className="font-medium text-gray-900">{invoice.organization_name}</span></span>
              </div>
            </div>
          )}

          <PaymentForm
            paymentDetails={paymentDetails}
            onSuccess={handlePaymentSuccess}
            savedPaymentMethods={savedPaymentMethods}
            stripeConfig={stripeConfig}
            stripe={stripePromise}
          />
        </div>
      </div>
    </div>
  );
}